/**
 * Hero Video Block — scroll-driven transition.
 *
 * The section is 220vh tall. The sticky inner wrapper pins to
 * the viewport while the user scrolls through the extra height.
 * progress 0 → 1 maps over that scroll travel.
 *
 *   0%–35%  : hero text fully visible
 *   35%–65% : hero text fades out / about panel fades in
 *   65%–100%: about panel fully visible
 *
 * @package UnderstrapChild
 */

function initHeroVideoBlock( section ) {
	if ( ! section ) return;

	var loadOnInteraction = section.getAttribute( 'data-load-on-interaction' ) === '1';
	var heroText   = section.querySelector( '.js-hero-text' );
	var aboutPanel = section.querySelector( '.js-hero-about' );
	var sticky     = section.querySelector( '.hero-video-block__sticky' );
	var bodyEl     = document.body;
	var isHomePage = !! ( bodyEl && ( bodyEl.classList.contains( 'home' ) || bodyEl.classList.contains( 'front-page' ) ) );
	var transitionMode = section.getAttribute( 'data-transition-mode' ) === 'slide' ? 'slide' : 'fade';
	if ( ! heroText || ! aboutPanel ) return;

	var watchBtn = section.querySelector( '.js-watch-video' );
	if ( watchBtn ) {
		watchBtn.addEventListener( 'click', function ( e ) {
			e.preventDefault();
			var target = section.offsetTop + section.offsetHeight * 0.6;
			window.scrollTo( { top: target, behavior: 'smooth' } );
		} );
	}

	// Fade out the poster once the Vimeo/YouTube iframe has finished loading.
	var poster = section.querySelector( '.hero-video-block__poster' );
	if ( poster ) {
		var embedForPoster = section.querySelector( '.hero-video-block__embed' );
		if ( embedForPoster ) {
			embedForPoster.addEventListener( 'load', function () {
				poster.classList.add( 'is-hidden' );
			} );
		}
	}

	if ( loadOnInteraction ) {
		var mediaStarted = false;

		function startMedia() {
			if ( mediaStarted ) return;
			mediaStarted = true;

			removeInteractionListeners();

			var embed = section.querySelector( '.hero-video-block__embed[data-src]' );
			if ( embed && ! embed.getAttribute( 'src' ) ) {
				embed.setAttribute( 'src', embed.getAttribute( 'data-src' ) );
			}

			var video = section.querySelector( '.hero-video-block__video' );
			if ( video ) {
				var source = video.querySelector( 'source[data-src]' );
				if ( source && ! source.getAttribute( 'src' ) ) {
					source.setAttribute( 'src', source.getAttribute( 'data-src' ) );
					video.load();
				}

				video.setAttribute( 'autoplay', '' );
				var playPromise = video.play();
				if ( playPromise && typeof playPromise.catch === 'function' ) {
					playPromise.catch( function () {} );
				}
			}
		}

		function removeInteractionListeners() {
			window.removeEventListener( 'mousemove', startMedia );
			window.removeEventListener( 'touchstart', startMedia );
			window.removeEventListener( 'scroll', startMedia );
			window.removeEventListener( 'keydown', startMedia );
		}

		window.addEventListener( 'mousemove', startMedia, { passive: true } );
		window.addEventListener( 'touchstart', startMedia, { passive: true } );
		window.addEventListener( 'scroll', startMedia, { passive: true } );
		window.addEventListener( 'keydown', startMedia );
	}

	var ticking = false;

	function update() {
		var rect     = section.getBoundingClientRect();
		var scrolled = -rect.top;
		var travel   = section.offsetHeight - window.innerHeight;
		var progress = travel > 0 ? Math.min( Math.max( scrolled / travel, 0 ), 1 ) : 0;

		if ( bodyEl ) {
			if ( isHomePage ) {
				var navOffsetVar = window.getComputedStyle( document.documentElement ).getPropertyValue( '--ov-nav-offset' );
				var navOffset    = parseFloat( navOffsetVar ) || 86;
				var pastHero     = rect.bottom <= navOffset;
				bodyEl.classList.toggle( 'has-scrolled-past-hero-video', pastHero );
			} else {
				bodyEl.classList.remove( 'has-scrolled-past-hero-video' );
			}
		}

		var FADE_START = 0.35;
		var FADE_END   = 0.65;

		var heroOpacity, aboutOpacity, heroY, aboutY;

		if ( transitionMode === 'slide' ) {
			var panelTravel = sticky ? sticky.offsetHeight : window.innerHeight;

			if ( progress <= FADE_START ) {
				heroOpacity  = 1;
				heroY        = 0;
				aboutOpacity = 0;
				aboutY       = panelTravel;
			} else if ( progress >= FADE_END ) {
				heroOpacity  = 0;
				heroY        = -panelTravel;
				aboutOpacity = 1;
				aboutY       = 0;
			} else {
				var slideT   = ( progress - FADE_START ) / ( FADE_END - FADE_START );
				heroOpacity  = 1;
				heroY        = -slideT * panelTravel;
				aboutOpacity = 1;
				aboutY       = ( 1 - slideT ) * panelTravel;
			}
		} else {
			if ( progress <= FADE_START ) {
				heroOpacity  = 1;
				heroY        = 0;
				aboutOpacity = 0;
				aboutY       = 30;
			} else if ( progress >= FADE_END ) {
				heroOpacity  = 0;
				heroY        = -30;
				aboutOpacity = 1;
				aboutY       = 0;
			} else {
				var t        = ( progress - FADE_START ) / ( FADE_END - FADE_START );
				heroOpacity  = 1 - t;
				heroY        = -t * 30;
				aboutOpacity = t;
				aboutY       = ( 1 - t ) * 30;
			}
		}

		heroText.style.opacity   = heroOpacity;
		heroText.style.transform = 'translateY(' + heroY + 'px)';
		heroText.setAttribute( 'aria-hidden', heroOpacity < 0.1 ? 'true' : 'false' );

		aboutPanel.style.opacity   = aboutOpacity;
		aboutPanel.style.transform = 'translateY(' + aboutY + 'px)';
		aboutPanel.setAttribute( 'aria-hidden', aboutOpacity < 0.1 ? 'true' : 'false' );

		if ( sticky ) {
			var radiusProgress = progress >= FADE_END
				? Math.min( ( progress - FADE_END ) / ( 1 - FADE_END ), 1 )
				: 0;
			var radius = Math.round( radiusProgress * 32 ) + 'px';
			sticky.style.borderBottomLeftRadius  = radius;
			sticky.style.borderBottomRightRadius = radius;
		}

		if ( aboutOpacity > 0.1 ) {
			aboutPanel.classList.add( 'is-visible' );
		} else {
			aboutPanel.classList.remove( 'is-visible' );
		}

		ticking = false;
	}

	window.addEventListener( 'scroll', function () {
		if ( ! ticking ) {
			window.requestAnimationFrame( update );
			ticking = true;
		}
	}, { passive: true } );

	update();
}

// ── Frontend ──────────────────────────────────────────────────────────────────
document.addEventListener( 'DOMContentLoaded', function () {
	initHeroVideoBlock( document.querySelector( '[data-hero-video]' ) );
} );

// ── Block editor preview (ACF) ────────────────────────────────────────────────
if ( typeof acf !== 'undefined' ) {
	acf.addAction( 'render_block_preview/type=hero-video-block', function ( $el ) {
		var root = ( $el && $el[0] ) ? $el[0].querySelector( '[data-hero-video]' ) : null;
		initHeroVideoBlock( root );
	} );
}
